import { cellToLatLng, latLngToCell } from "h3-js"

import { get_projection, latlon_tuple_to_obj } from "../../dev/projection"
import { CapacityFactorData } from "../interface"
import { battery_plants_data } from "./battery_plants_data"
import { gas_plants_data } from "./gas_plant_data"
import { hydro_plants_data } from "./hydro_plant_data"
import { AggregatedBuiltPowerPlant, AggregatedBuiltPowerPlantsByH3R4Id, AggregatedPowerPlantDataByH3R4CellV1, AggregatedPowerPlantDataV1, PowerPlant } from "./interface"
import { nuclear_plants } from "./nuclear_plants_data"
import {
    parse_battery_data,
    parse_gas_data,
    parse_hydro_data,
    parse_solar_farm_data,
    parse_wind_farm_data,
} from "./parse_data"
import { solar_farms } from "./solar_farm_data"
import { offshore_wind_farms, onshore_wind_farms } from "./wind_farm_data"



export const initial_power_plants_data: PowerPlant[] = [
    ...offshore_wind_farms.map(parse_wind_farm_data),
    ...onshore_wind_farms.map(parse_wind_farm_data),
    ...solar_farms.map(parse_solar_farm_data),
    ...nuclear_plants,
    ...battery_plants_data.map(parse_battery_data),
    ...hydro_plants_data.map(parse_hydro_data),
    ...gas_plants_data.map(parse_gas_data),
]



export const H3_RESOLUTION = 4

export function map_power_plants_by_h3r4_id(plants: PowerPlant[]): Record<string, PowerPlant[]>
{
    const h3r4_id_to_power_plants: Record<string, PowerPlant[]> = {}

    plants.forEach(plant =>
    {
        const h3r4_id = latLngToCell(plant.lat, plant.lon, H3_RESOLUTION)

        h3r4_id_to_power_plants[h3r4_id] = h3r4_id_to_power_plants[h3r4_id] || []
        h3r4_id_to_power_plants[h3r4_id].push(plant)
    })

    return h3r4_id_to_power_plants
}

export const initial_power_plants_by_h3r4_id = map_power_plants_by_h3r4_id(initial_power_plants_data)


/**
 * @deprecated - power plants data should not be dependent on capacity factor
 * data, instead this validation should sit in the model setup code.
 */
export function empty_aggregated_power_plant_data_v1(
    h3r4_id: string,
    wind_capacity_factor: CapacityFactorData,
    solar_pv_capacity_factor: CapacityFactorData,
): AggregatedPowerPlantDataV1
{
    const lat_lon = latlon_tuple_to_obj(cellToLatLng(h3r4_id))
    const projection = get_projection()
    const xy = projection(lat_lon)
    if (!xy) throw new Error(`Failed to project lat/lon for h3_id ${h3r4_id}`)

    const h3r4_index_wind = wind_capacity_factor.h3r4_cell_id_to_index.get(h3r4_id)
    if (h3r4_index_wind === undefined) throw new Error(`H3 cell ID ${h3r4_id} not found in wind capacity factor data`)
    const h3r4_index_solar = solar_pv_capacity_factor.h3r4_cell_id_to_index.get(h3r4_id)

    const data: AggregatedPowerPlantDataV1 = {
        h3r4_id, lat_lon, xy,
        wind:     { starting_count: 0, count: 0, starting_capacity_MW: 0, capacity_MW: 0, starting_area_km2: 0, area_km2: 0, h3r4_capacity_factor_index: h3r4_index_wind },
        solar:    { starting_count: 0, count: 0, starting_capacity_MW: 0, capacity_MW: 0, starting_area_km2: 0, area_km2: 0, h3r4_capacity_factor_index: h3r4_index_solar },
        gas:     { starting_count: 0, count: 0, starting_capacity_MW: 0, capacity_MW: 0 },
        nuclear: { starting_count: 0, count: 0, starting_capacity_MW: 0, capacity_MW: 0 },
        battery: { starting_count: 0, count: 0, starting_capacity_MW: 0, capacity_MW: 0, store_capacity_MWh: 0, stored_MWh: 0 },
        hydro_pumped_storage: { starting_count: 0, count: 0, starting_capacity_MW: 0, capacity_MW: 0, store_capacity_MWh: 0, stored_MWh: 0 },
        hydro_river: { starting_count: 0, count: 0, starting_capacity_MW: 0, capacity_MW: 0 },
    }

    return data
}


export function empty_aggregated_power_plant_data(): AggregatedBuiltPowerPlant
{
    const data: AggregatedBuiltPowerPlant = {
        gas:      { count: 0, capacity_MW: 0, area_km2: 0 },
        hydro_river: { count: 0, capacity_MW: 0, area_km2: 0 },
        nuclear:  { count: 0, capacity_MW: 0, area_km2: 0 },
        solar:    { count: 0, capacity_MW: 0, area_km2: 0 },
        wind:     { count: 0, capacity_MW: 0, area_km2: 0 },

        battery:  { count: 0, capacity_MW: 0, area_km2: 0, store_capacity_MWh: 0 },
        hydro_pumped_storage: { count: 0, capacity_MW: 0, area_km2: 0, store_capacity_MWh: 0 },
    }

    return data
}

/**
 * @deprecated - power plants data should not be dependent on capacity factor
 * data, instead this validation should sit in the model setup code.
 */
export function aggregate_power_plants_by_h3r4_id_v1(
    plants_by_h3r4_id: Record<string, PowerPlant[]>,
    wind_capacity_factor: CapacityFactorData,
    solar_pv_capacity_factor: CapacityFactorData,
): AggregatedPowerPlantDataByH3R4CellV1
{
    const aggregated: AggregatedPowerPlantDataByH3R4CellV1 = {}

    for (const [h3r4_id, plants] of Object.entries(plants_by_h3r4_id))
    {
        const data = empty_aggregated_power_plant_data_v1(h3r4_id, wind_capacity_factor, solar_pv_capacity_factor)

        const active_plants = get_active_power_plants(plants, 2026)

        active_plants.forEach(plant =>
        {
            const capacity = plant.nameplate_capacity_MW

            if (plant.type === "wind_farm")
            {
                data.wind.starting_count++
                data.wind.count++
                data.wind.starting_capacity_MW += capacity
                data.wind.capacity_MW += capacity
                data.wind.starting_area_km2! += plant.area_km2
                data.wind.area_km2! += plant.area_km2
            }
            else if (plant.type === "solar_farm")
            {
                data.solar.starting_count++
                data.solar.count++
                data.solar.starting_capacity_MW += capacity
                data.solar.capacity_MW += capacity
                data.solar.starting_area_km2! += plant.area_km2
                data.solar.area_km2! += plant.area_km2
            }
            else if (plant.type === "gas")
            {
                data.gas.starting_count++
                data.gas.count++
                data.gas.starting_capacity_MW += capacity
                data.gas.capacity_MW += capacity
            }
            else if (plant.type === "nuclear")
            {
                data.nuclear.starting_count++
                data.nuclear.count++
                data.nuclear.starting_capacity_MW += capacity
                data.nuclear.capacity_MW += capacity
            }
            else if (plant.type === "battery")
            {
                data.battery.starting_count++
                data.battery.count++
                data.battery.starting_capacity_MW += capacity
                data.battery.capacity_MW += capacity
                data.battery.store_capacity_MWh! += plant.storage_MWH
            }
            else if (plant.type === "hydro")
            {
                if (plant.storage_MWH && plant.storage_MWH > 0)
                {
                    data.hydro_pumped_storage.starting_count++
                    data.hydro_pumped_storage.count++
                    data.hydro_pumped_storage.starting_capacity_MW += capacity
                    data.hydro_pumped_storage.capacity_MW += capacity
                    data.hydro_pumped_storage.store_capacity_MWh! += plant.storage_MWH
                }
                else
                {
                    data.hydro_river.starting_count++
                    data.hydro_river.count++
                    data.hydro_river.starting_capacity_MW += capacity
                    data.hydro_river.capacity_MW += capacity
                }
            }
            // @ts-expect-error
            else console.error(`Unknown plant type: ${plant.type}`)
        })

        aggregated[h3r4_id] = data
    }

    return aggregated
}


export function aggregate_power_plants_by_h3r4_id(
    plants_by_h3r4_id: Record<string, PowerPlant[]>,
): AggregatedBuiltPowerPlantsByH3R4Id
{
    const aggregated: AggregatedBuiltPowerPlantsByH3R4Id = {}

    for (const [h3r4_id, plants] of Object.entries(plants_by_h3r4_id))
    {
        const data = empty_aggregated_power_plant_data()

        const active_plants = get_active_power_plants(plants, 2026)

        active_plants.forEach(plant =>
        {
            const capacity = plant.nameplate_capacity_MW

            if (plant.type === "wind_farm")
            {
                data.wind.count++
                data.wind.capacity_MW += capacity
                data.wind.area_km2 += plant.area_km2
            }
            else if (plant.type === "solar_farm")
            {
                data.solar.count++
                data.solar.capacity_MW += capacity
                data.solar.area_km2 += plant.area_km2
            }
            else if (plant.type === "gas")
            {
                data.gas.count++
                data.gas.capacity_MW += capacity
                data.gas.area_km2 += 0  // TODO, find area required
            }
            else if (plant.type === "nuclear")
            {
                data.nuclear.count++
                data.nuclear.capacity_MW += capacity
                data.nuclear.area_km2 += 0  // TODO, find area required
            }
            else if (plant.type === "battery")
            {
                data.battery.count++
                data.battery.capacity_MW += capacity
                data.battery.area_km2 += 0  // TODO, find area required
                data.battery.store_capacity_MWh! += plant.storage_MWH
            }
            else if (plant.type === "hydro")
            {
                if (plant.storage_MWH && plant.storage_MWH > 0)
                {
                    data.hydro_pumped_storage.count++
                    data.hydro_pumped_storage.capacity_MW += capacity
                    data.hydro_pumped_storage.area_km2 += 0  // TODO, find area required
                    data.hydro_pumped_storage.store_capacity_MWh! += plant.storage_MWH
                }
                else
                {
                    data.hydro_river.count++
                    data.hydro_river.capacity_MW += capacity
                    data.hydro_river.area_km2 += 0  // TODO, find area required
                }
            }
            // @ts-expect-error
            else console.error(`Unknown plant type: ${plant.type}`)
        })

        aggregated[h3r4_id] = data
    }

    return aggregated
}


function get_active_power_plants(power_plants: PowerPlant[], year: number): PowerPlant[]
{
    return power_plants.filter(plant =>
        (
            plant.status === "under_construction"
            ||
            (
                plant.operational_year &&
                plant.operational_year <= year
            )
        )
        &&
        (plant.decommissioned_year === undefined || plant.decommissioned_year > year)
    )
}
